
import { IUserRepository } from '@/shared/contracts/repositories/IUserRepository';
import { UserProfileRepository } from './user/UserProfileRepository';
import { UserPrivacyRepository } from './user/UserPrivacyRepository';
import { supabase } from '@/integrations/supabase/client';
import type { Profile, CreateProfileData, UpdateProfileData, PrivacySettings } from '@/shared/contracts/repositories/IUserRepository';

export class UserRepository implements IUserRepository {
  private profileRepository: UserProfileRepository;
  private privacyRepository: UserPrivacyRepository;

  constructor() {
    this.profileRepository = new UserProfileRepository();
    this.privacyRepository = new UserPrivacyRepository();
  }

  // Profile operations - delegate to ProfileRepository
  async findByEmail(email: string): Promise<Profile | null> {
    return this.profileRepository.findByEmail(email);
  }

  async findByUserId(userId: string): Promise<Profile[]> {
    const profile = await this.profileRepository.findByUserIdAndId(userId, userId);
    return profile ? [profile] : [];
  }

  async findByUserIdAndId(userId: string, id: string): Promise<Profile | null> {
    return this.profileRepository.findByUserIdAndId(userId, id);
  }

  async createProfile(userId: string, data: CreateProfileData): Promise<Profile> {
    return this.profileRepository.createProfile(userId, data);
  }

  async createForUser(userId: string, data: CreateProfileData): Promise<Profile> {
    return this.profileRepository.createProfile(userId, data);
  }

  async updateProfile(userId: string, data: UpdateProfileData): Promise<Profile> {
    return this.profileRepository.updateProfile(userId, data);
  }

  async updateForUser(userId: string, id: string, data: UpdateProfileData): Promise<Profile> {
    return this.profileRepository.updateProfile(userId, data);
  }

  async deleteForUser(userId: string, id: string): Promise<boolean> {
    return this.profileRepository.deleteProfile(userId);
  }

  // Privacy operations - delegate to PrivacyRepository
  async getPrivacySettings(userId: string): Promise<PrivacySettings | null> {
    return this.privacyRepository.getPrivacySettings(userId);
  }

  async updatePrivacySettings(userId: string, settings: Partial<PrivacySettings>): Promise<PrivacySettings> {
    return this.privacyRepository.updatePrivacySettings(userId, settings);
  }

  // Cache management - delegate to ProfileRepository
  async clearUserCache(userId: string): Promise<void> {
    this.profileRepository.clearUserCache(userId);
  }

  async refreshUserData(userId: string): Promise<Profile | null> {
    return this.profileRepository.refreshUserData(userId);
  }

  // Simple repository methods
  async findById(id: string): Promise<Profile | null> {
    return this.profileRepository.findByUserIdAndId(id, id);
  }

  async findMany(filters?: Record<string, string | number | boolean>): Promise<Profile[]> {
    try {
      // Simple query using static import to avoid chunking conflicts
      const { data, error } = await supabase.from('profiles').select('*');
      
      if (error) throw error;
      return (data || []) as Profile[];
    } catch (error) {
      console.error('❌ Erro ao buscar perfis:', error);
      return [];
    }
  }

  async create(data: CreateProfileData): Promise<Profile> {
    throw new Error('Use createProfile or createForUser instead');
  }

  async update(id: string, data: UpdateProfileData): Promise<Profile> {
    return this.profileRepository.updateProfile(id, data);
  }

  async delete(id: string): Promise<boolean> {
    return this.profileRepository.deleteProfile(id);
  }
}

// Re-export types for convenience
export type { Profile, CreateProfileData, UpdateProfileData, PrivacySettings };
